import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  InvoiceInitialState,
  InvoicesSortPayloadType,
  SortInvoicesBy,
} from './invoices.types';
import { InvoicesKey, multiSort } from './invoices.const';
import { fetchProjectInvoicesData } from './invoices.thunks';
import { DataState } from '../../types/asyncData';
import { InvoiceState } from '../../features/invoices/Invoices.const';
import { SortOrder } from '../../types';

const initialState: InvoiceInitialState = {
  invoices: {
    state: DataState.Idle,
    data: null,
  },
  areFiltersApplied: true,
  filterBy: {
    invoiceState: InvoiceState.OVERDUE,
    projectNames: [],
    showZeroInvoices: false,
  },
  isFilterOpen: false,
  sort: [{ by: SortInvoicesBy.INVOICE_DUE_DATE, order: SortOrder.Descending }],
};

export const invoicesSlice = createSlice({
  name: InvoicesKey,
  initialState,
  reducers: {
    setFilterByInvoiceState: (
      state,
      { payload }: PayloadAction<InvoiceState>,
    ) => {
      const isAllWithoutProjectsAndZero =
        payload === InvoiceState.ALL &&
        state.filterBy.projectNames.length === 0 &&
        !state.filterBy.showZeroInvoices;

      state.areFiltersApplied = !isAllWithoutProjectsAndZero;
      state.filterBy.invoiceState = payload;
    },
    setFilterByProjectName: (state, { payload }: PayloadAction<string>) => {
      if (!state.filterBy.projectNames.includes(payload)) {
        state.filterBy.projectNames.push(payload);
        state.areFiltersApplied =
          state.filterBy.projectNames.length > 0 ||
          state.filterBy.invoiceState !== InvoiceState.ALL ||
          state.filterBy.showZeroInvoices;
      }
    },
    removeProjectFromFilter: (state, { payload }: PayloadAction<string>) => {
      state.filterBy.projectNames = state.filterBy.projectNames.filter(
        (x) => x !== payload,
      );
      state.areFiltersApplied =
        state.filterBy.projectNames.length > 0 ||
        state.filterBy.invoiceState !== InvoiceState.ALL ||
        state.filterBy.showZeroInvoices;
    },
    resetFilters: (state, { payload }: PayloadAction<InvoiceState>) => {
      state.filterBy = {
        invoiceState: payload,
        projectNames: [],
        showZeroInvoices: false,
      };
      state.areFiltersApplied = payload !== InvoiceState.ALL;
    },
    resetInvoices: (state) => {
      state.invoices.data = null;
    },
    setShowZeroInvoices: (state, { payload }: PayloadAction<boolean>) => {
      state.filterBy.showZeroInvoices = payload;
      state.areFiltersApplied =
        state.filterBy.projectNames.length > 0 ||
        state.filterBy.invoiceState !== InvoiceState.ALL ||
        state.filterBy.showZeroInvoices;
    },
    openFilter: (state) => {
      state.isFilterOpen = true;
    },
    closeFilter: (state) => {
      state.isFilterOpen = false;
    },
    upsertToSort: (
      state,
      { payload }: PayloadAction<InvoicesSortPayloadType>,
    ) => {
      const elem = state.sort.find((s) => s.by === payload.newSortBy);
      let newElem;

      // We have 3 clicks, one for ascending, then for descending, then for removing the sort
      if (elem && elem.order === SortOrder.Descending) {
        state.sort = state.sort.filter((s) => s.by !== elem.by);

        if (!state.invoices.data) return;

        state.invoices.data = multiSort(state.invoices.data, state.sort);
        return;
      }

      if (!elem) {
        newElem = { by: payload.newSortBy, order: SortOrder.Ascending };
        state.sort.push(newElem);
      } else {
        elem.order =
          elem.order === SortOrder.Ascending
            ? SortOrder.Descending
            : SortOrder.Ascending;
      }

      if (!state.invoices.data) return;

      state.invoices.data = multiSort(state.invoices.data, state.sort);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProjectInvoicesData.pending, (state) => {
        state.invoices = {
          state: DataState.Pending,
          data: null,
        };
      })
      .addCase(fetchProjectInvoicesData.fulfilled, (state, { payload }) => {
        const sortedInvoices = multiSort(payload, state.sort);
        state.invoices = {
          state: DataState.Fulfilled,
          data: sortedInvoices,
        };
      })
      .addCase(fetchProjectInvoicesData.rejected, (state) => {
        state.invoices = {
          state: DataState.Rejected,
          data: null,
          error: {
            message: 'Oops. Something went wrong. :(',
          },
        };
      });
  },
});
